import { Component } from '@theme/component';

const STORAGE_PREFIX = 'siy-design:';
// Editable strokes are kept client-side keyed by the cart line, NOT as a line-item
// property — so they never ride along on the submitted order. Editing only happens
// in the cart (pre-checkout); a different device/browser simply restores no drawing.
// Keep this string in sync with the prune in cart.js.
const LINE_STORAGE_PREFIX = 'siy-design-line:';
const FALLBACK_SLATE = '#2D2B27';
const DEFAULT_BRUSH_SIZE = 4;
// The eraser gets its own, much larger width. At the 4px brush width it was
// effectively unusable for correcting a drawing, which is what the "hard to use
// accurately" feedback was really about. Both are CSS px: strokes are stored as a
// fraction of the canvas CSS width and replayed against the backing width, so the
// rendered line lands back at exactly this many CSS px.
const ERASER_SIZE = 18;

class SheIsYouCustomizer extends Component {
  onConnect() {
    try {
      this.variants = JSON.parse(this.querySelector('[data-siy-variants]').textContent);
      this.config = JSON.parse(this.querySelector('[data-siy-config]').textContent);
    } catch (_error) {
      this.variants = [];
      this.config = {};
    }
    if (!Array.isArray(this.variants) || this.variants.length === 0) {
      const stageError = this.querySelector('[ref="stageError"]');
      if (stageError) stageError.hidden = false;
      return; // leave the element inert rather than half-wired
    }
    if (this.wired) return;
    this.wired = true;
    this.form = this.querySelector('form[data-product-form]');
    // Native constraint validation runs BEFORE the submit event, so an empty
    // required note would block the handler entirely and show the browser's
    // generic bubble instead of our copy. Opt out now that JS owns the gate;
    // without JS, `required` on the textarea stays live as the backstop.
    this.form.noValidate = true;
    this.storageKey = STORAGE_PREFIX + (this.dataset.productHandle || 'product');

    try {
      this.bases = JSON.parse(this.querySelector('[data-siy-bases]').textContent);
    } catch (_error) {
      this.bases = {};
    }

    const names = (this.config.optionNames || []).map((n) => String(n).toLowerCase());
    // Which option carries the body shape (Slim / Curvy / Plus) — it picks which
    // ivory base photo we show for each side.
    this.shapeIdx = names.findIndex((n) => n.includes('shape') || n.includes('size'));

    // The base preview is always ivory (light), so strokes are always dark slate
    // for contrast. Resolve the token once so strokes + PNG export stay on-brand.
    this.slate = this.resolveToken('--color-slate', FALLBACK_SLATE);

    // Front and back keep independent stroke + redo stacks. this.state.strokes and
    // this.state.redo are aliases of the active side's arrays so the rest of the
    // drawing code is unchanged; useSide() re-points them.
    this.sides = {
      front: { strokes: [], redo: [] },
      back: { strokes: [], redo: [] },
    };
    this.activeSide = 'front';
    this.state = {
      tool: 'brush',
      drawColor: this.slate,
      brushSize: DEFAULT_BRUSH_SIZE,
      strokes: this.sides.front.strokes,
      redo: this.sides.front.redo,
    };
    this.activeVariant = this.variants.find((v) => v.available) || this.variants[0];
    this.activeStroke = null;
    this.exporting = false;
    this.pendingImageSrc = null;
    this.zoom = 1;

    // The toast has to outlive the panel: close() hides this whole element, so a
    // toast inside it would vanish with the panel on the add-to-bag path. Move it
    // to <body> and hold a direct reference — refs are only collected while it is
    // still a descendant, so showToast() must not read this.refs afterwards.
    this.toastEl = this.refs.toast;
    if (this.toastEl) document.body.appendChild(this.toastEl);

    this.boundHandlers = {
      imageLoaded: () => this.handleImageLoaded(),
      imageError: () => this.handleImageError(),
      pointerDown: (e) => this.startStroke(e),
      pointerMove: (e) => this.extendStroke(e),
      pointerUp: (e) => this.endStroke(e),
      cartAdded: (e) => this.handleCartAdded(e),
      openClick: (e) => this.handleOpenClick(e),
      keydown: (e) => this.handleKeydown(e),
    };
    this.refs.baseImage.addEventListener('load', this.boundHandlers.imageLoaded);
    this.refs.baseImage.addEventListener('error', this.boundHandlers.imageError);
    // product-form.js dispatches this on document after a successful add.
    document.addEventListener('theme:cart:added', this.boundHandlers.cartAdded);
    // The CUSTOMISE trigger lives on the PDP, outside this element.
    document.addEventListener('click', this.boundHandlers.openClick);
    document.addEventListener('keydown', this.boundHandlers.keydown);

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-siy-close]')) {
        this.close();
        return;
      }

      const zoomButton = event.target.closest('[data-siy-zoom]');
      if (zoomButton) {
        this.adjustZoom(zoomButton.dataset.siyZoom === 'in' ? 0.5 : -0.5);
        return;
      }

      const sideButton = event.target.closest('[data-siy-side]');
      if (sideButton) {
        this.switchSide(sideButton.dataset.siySide);
        return;
      }

      const toolButton = event.target.closest('[data-siy-tool]');
      if (toolButton) {
        this.state.tool = toolButton.dataset.siyTool;
        this.querySelectorAll('[data-siy-tool]').forEach((btn) => {
          const active = btn === toolButton;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', String(active));
        });
        this.updateCanvasCursor();
        return;
      }

      const actionButton = event.target.closest('[data-siy-action]');
      if (actionButton) {
        const action = actionButton.dataset.siyAction;
        if (action === 'undo') this.undo();
        if (action === 'redo') this.redo();
        if (action === 'clear') this.requestClear(actionButton);
        if (action === 'retry-image') {
          this.refs.stageError.hidden = true;
          this.preloadBaseImages(this.shapeKey);
          this.syncImage(this.baseUrl(this.activeSide), true);
        }
      }
    });

    this.ctx = this.refs.canvas.getContext('2d');
    this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
    this.resizeObserver.observe(this.refs.baseImage);

    this.refs.canvas.addEventListener('pointerdown', this.boundHandlers.pointerDown);
    this.refs.canvas.addEventListener('pointermove', this.boundHandlers.pointerMove);
    this.refs.canvas.addEventListener('pointerup', this.boundHandlers.pointerUp);
    this.refs.canvas.addEventListener('pointercancel', this.boundHandlers.pointerUp);

    this.refs.saveButton.addEventListener('click', () => this.saveDesign());
    this.refs.downloadButton.addEventListener('click', () => this.downloadDesign());

    this.refs.addButton.addEventListener('click', (event) => {
      // Click, not submit: a submit on a form with action="/cart/add" makes
      // Shopify's web pixels emit a second product_added_to_cart. product-form.js
      // skips this form (data-product-form-custom) and adds on our request.
      event.preventDefault();

      if (this.exporting) return;

      const variant = this.currentVariant();
      if (!variant || !variant.available) {
        this.showFormError('This combination is currently unavailable.');
        return;
      }

      // The `required` attribute never fires here: nothing submits the form, so
      // the browser runs no constraint validation. Gate before the export so a
      // blank note costs no canvas work.
      const notes = this.refs.notesField.value.trim();
      if (!notes) {
        // The gate is shared by both paths, but the action named in the copy must match
        // the button the customer just pressed. A pre-existing cart line carries no note,
        // so Edit Order is exactly where a blank note is most likely to be hit.
        this.showFormError(
          this.editMode
            ? 'Please tell us about your scars before updating your order.'
            : 'Please tell us about your scars before adding to your basket.'
        );
        this.refs.notesField.focus();
        // The panel is a scrollable slide-in, so focus alone can leave the field
        // offscreen — a dead end for the customer.
        this.refs.notesField.scrollIntoView({ block: 'center' });
        return;
      }

      this.exporting = true;
      const addButton = this.refs.addButton;
      const originalLabel = addButton.textContent;
      addButton.disabled = true;
      addButton.textContent = 'PREPARING...';

      Promise.all([this.exportSide('front'), this.exportSide('back')])
        .then(([frontBlob, backBlob]) => {
          // Both DataTransfers must be set before the add is requested; edit
          // mode's new FormData(this.form) then picks up both file inputs for free.
          this.attachFile(this.refs.designFileInput, frontBlob, 'she-is-you-front.png');
          this.attachFile(this.refs.designFileInputBack, backBlob, 'she-is-you-back.png');
          // pin the submitted id to the variant we validated, in case the
          // selection changed during the async export
          this.refs.variantIdInput.value = String(variant.id);
          if (this.editMode) {
            this.submitUpdate(addButton, originalLabel);
            return;
          }
          addButton.disabled = false;
          addButton.textContent = originalLabel;
          this.exporting = false;
          document.dispatchEvent(new CustomEvent('theme:cart:add-request', {
            detail: { form: this.form },
          }));
        })
        .catch(() => {
          addButton.disabled = false;
          addButton.textContent = originalLabel;
          this.exporting = false;
          this.showFormError('We could not prepare your design. Please try again.');
        });
    });

    this.setVariant(this.activeVariant, { reloadImage: false });
    this.restoreSaved();
    this.maybeEnterEditFromUrl();
  }

  onDisconnect() {
    this.resizeObserver?.disconnect();
    if (this.boundHandlers) {
      this.refs.baseImage?.removeEventListener('load', this.boundHandlers.imageLoaded);
      this.refs.baseImage?.removeEventListener('error', this.boundHandlers.imageError);
      this.refs.canvas?.removeEventListener('pointerdown', this.boundHandlers.pointerDown);
      this.refs.canvas?.removeEventListener('pointermove', this.boundHandlers.pointerMove);
      this.refs.canvas?.removeEventListener('pointerup', this.boundHandlers.pointerUp);
      this.refs.canvas?.removeEventListener('pointercancel', this.boundHandlers.pointerUp);
      document.removeEventListener('theme:cart:added', this.boundHandlers.cartAdded);
      document.removeEventListener('click', this.boundHandlers.openClick);
      document.removeEventListener('keydown', this.boundHandlers.keydown);
    }
    document.body.classList.remove('siy-locked');
    // Never leave the page invisible if the element goes away while isolated.
    document.body.classList.remove('siy-isolated');
    window.clearTimeout(this.isolateTimer);
    window.clearTimeout(this.clearArmTimer);
    window.clearTimeout(this.toastTimer);
    window.clearTimeout(this.formErrorTimer);
    window.clearTimeout(this.closeTimer);
    // Reparented to <body> in onConnect, so it will not be removed with the element.
    this.toastEl?.remove();
  }

  resolveToken(name, fallback) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  get rootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  /* ---- Panel open / close ------------------------------------------------ */

  handleOpenClick(event) {
    const trigger = event.target.closest('[data-siy-open]');
    if (!trigger || trigger.disabled) return;
    event.preventDefault();
    this.open();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.classList.contains('is-open')) {
      this.close();
    }
  }

  open() {
    // Read the variant the customer chose on the PDP (variant-picker keeps the
    // hidden input in sync). Fall back to the first available variant.
    const pdpInput = document.querySelector('[data-variant-id-input]');
    const chosenId = pdpInput ? Number(pdpInput.value) : null;
    const variant =
      this.variants.find((v) => v.id === chosenId) ||
      this.variants.find((v) => v.available) ||
      this.variants[0];

    // Always (re)open on the front; setVariant then loads the front base image.
    this.useSide('front');
    this.setVariant(variant, { reloadImage: true });

    this.hidden = false;
    document.body.classList.add('siy-locked');
    this.previouslyFocused = document.activeElement;
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.resizeCanvas();
      this.refs.closeButton?.focus();
    });
    // Stop the PDP painting once the panel covers it, so iOS has nothing to
    // composite into the strips behind its status bar and toolbar. Deferred past
    // the slide-in (see the .siy-isolated rules in she-is-you.css).
    window.clearTimeout(this.isolateTimer);
    this.isolateTimer = window.setTimeout(() => {
      document.body.classList.add('siy-isolated');
    }, 360);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('siy-locked');
    // Must drop before the slide-out starts, or the PDP stays blank behind it.
    window.clearTimeout(this.isolateTimer);
    document.body.classList.remove('siy-isolated');
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.hidden = true;
    }, 320);
    if (this.previouslyFocused instanceof HTMLElement) {
      this.previouslyFocused.focus();
    }
  }

  enterEditMode({ lineKey, payload }) {
    this.editMode = true;
    this.editLineKey = lineKey;
    this.applyPayload(payload);
    this.syncAddButton(); // relabels to UPDATE YOUR ORDER (Step 5)
  }

  /* ---- Per-line stroke storage (localStorage, off the order) ------------- */

  saveLineDesign(lineKey) {
    if (!lineKey) return;
    try {
      localStorage.setItem(LINE_STORAGE_PREFIX + lineKey, JSON.stringify(this.strokesPayload()));
    } catch (_error) {
      // storage full / unavailable — degrade to no restore for this line
    }
  }

  readLineDesign(lineKey) {
    try {
      const raw = localStorage.getItem(LINE_STORAGE_PREFIX + lineKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_error) {
      return null;
    }
  }

  removeLineDesign(lineKey) {
    if (!lineKey) return;
    try {
      localStorage.removeItem(LINE_STORAGE_PREFIX + lineKey);
    } catch (_error) {
      // ignore
    }
  }

  maybeEnterEditFromUrl() {
    const key = new URLSearchParams(location.search).get('siy_edit');
    if (!key) return;
    fetch(`${this.rootUrl}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const line = (cart.items || []).find((item) => item.key === key);
        if (!line) return; // item gone (e.g. removed elsewhere) — load PDP normally
        const props = line.properties || {};
        // Strokes live in localStorage keyed by the cart line (kept off the order).
        // Absent on another device/browser → blank canvas; notes + variant still restore.
        // Accept v3 ({sides}) and legacy v2 ({strokes}); anything else → blank both sides.
        let payload = this.readLineDesign(key);
        if (!payload || (!payload.sides && !Array.isArray(payload.strokes))) {
          payload = { sides: { front: [], back: [] } };
        }
        payload.notes = props['Tell us about your scars'] || '';
        this.open();
        this.enterEditMode({ lineKey: key, payload });
      })
      .catch(() => {}); // network failure — leave the PDP as a normal page
  }

  /* ---- Variant + brush colour ------------------------------------------- */

  setVariant(variant, { reloadImage } = {}) {
    if (!variant) return;
    this.activeVariant = variant;
    this.refs.variantIdInput.value = String(variant.id);
    this.shapeKey = this.shapeForVariant(variant);
    this.syncAddButton();
    if (reloadImage) {
      this.preloadBaseImages(this.shapeKey);
      this.syncImage(this.baseUrl(this.activeSide), false);
    }
  }

  shapeForVariant(variant) {
    const value = (this.shapeIdx >= 0 ? variant.options[this.shapeIdx] : '') || '';
    const shape = value.toLowerCase();
    if (shape.includes('curvy')) return 'curvy';
    if (shape.includes('plus')) return 'plus';
    return 'slim';
  }

  baseUrl(side) {
    const shape = this.bases[this.shapeKey] || this.bases.slim || {};
    return shape[side] || shape.front || '';
  }

  // Off-screen Images used only by the PNG export, so both sides can be exported
  // regardless of which one is on screen. crossOrigin is set before src so the
  // export canvas stays untainted (toBlob would otherwise throw).
  preloadBaseImages(shape) {
    this.baseImageCache = { front: null, back: null };
    const urls = this.bases[shape] || this.bases.slim || {};
    for (const side of ['front', 'back']) {
      if (!urls[side]) continue;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = urls[side];
      this.baseImageCache[side] = img;
    }
  }

  switchSide(side) {
    if (side !== 'front' && side !== 'back') return;
    if (side === this.activeSide) return;
    // abandon any half-drawn stroke so it doesn't land on the other side
    this.activeStroke = null;
    this.activePointerId = null;
    this.useSide(side);
    this.syncImage(this.baseUrl(side), false);
    // Repaint now — syncImage no-ops on an unchanged src, so a cached re-show
    // might never fire `load`; handleImageLoaded repaints again once the
    // (differently sized) side image settles.
    this.renderAll();
  }

  // Point the active stroke/redo aliases at a side's arrays and sync the UI.
  useSide(side) {
    this.activeSide = side;
    this.state.strokes = this.sides[side].strokes;
    this.state.redo = this.sides[side].redo;
    this.syncSideButtons();
    this.updateToolButtons();
  }

  syncSideButtons() {
    this.querySelectorAll('[data-siy-side]').forEach((btn) => {
      const active = btn.dataset.siySide === this.activeSide;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }

  // Replace the active side's stroke/redo arrays while keeping this.state aliased
  // to this.sides (a bare `this.state.strokes = []` would detach the alias).
  setActiveStrokes(strokes, redo) {
    this.sides[this.activeSide].strokes = strokes;
    this.sides[this.activeSide].redo = redo;
    this.state.strokes = strokes;
    this.state.redo = redo;
  }

  attachFile(input, blob, name) {
    const file = new File([blob], name, { type: 'image/png' });
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
  }

  syncAddButton() {
    const variant = this.activeVariant;
    const available = variant && variant.available;
    const label = this.editMode ? 'UPDATE YOUR ORDER' : 'ADD TO BASKET';
    this.refs.addButton.textContent = available ? label : 'UNAVAILABLE';
    this.refs.addButton.classList.toggle('is-unavailable', !available);
    this.refs.addButton.disabled = !available;
  }

  currentVariant() {
    return this.activeVariant || null;
  }

  async submitUpdate(addButton, originalLabel) {
    addButton.disabled = true;
    addButton.textContent = 'UPDATING...';
    try {
      const addResponse = await fetch(`${this.rootUrl}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!addResponse.ok) throw new Error('add-failed');
      const addedLine = await addResponse.json();

      // Carry the editable strokes to the new line (localStorage, off the order)
      // and drop the old line's entry.
      this.saveLineDesign(addedLine.key);
      this.removeLineDesign(this.editLineKey);

      // Remove the old line by key. A failure here is non-fatal: the new line
      // carries the "Updated" badge so the customer can spot/remove the stale one.
      try {
        const removeResponse = await fetch(`${this.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: this.editLineKey, quantity: 0 }),
        });
        if (!removeResponse.ok) {
          console.warn('siy: failed to remove old cart line', this.editLineKey, removeResponse.status);
        }
      } catch (error) {
        console.warn('siy: failed to remove old cart line', this.editLineKey, error);
      }

      const cartUrl = `${this.rootUrl.replace(/\/$/, '')}/cart`;
      this.exporting = false;
      window.location.assign(`${cartUrl}?siy_updated=${encodeURIComponent(addedLine.key)}`);
    } catch (_error) {
      addButton.disabled = false;
      addButton.textContent = originalLabel;
      this.exporting = false;
      this.showFormError('We could not update your order. Please try again.');
    }
  }

  syncImage(src, force) {
    if (!src) return;
    // Normalise to absolute URL so comparisons against img.src (always absolute) work
    const absSrc = new URL(src, location.href).href;
    if (!force && this.refs.baseImage.src === absSrc) return;
    this.pendingImageSrc = absSrc;
    this.refs.stage.classList.add('is-loading');
    this.refs.baseImage.src = absSrc;
  }

  handleImageLoaded() {
    if (this.pendingImageSrc && this.refs.baseImage.src !== this.pendingImageSrc) return;
    this.refs.stage.classList.remove('is-loading');
    this.refs.stageError.hidden = true;
    this.resizeCanvas();
  }

  handleImageError() {
    if (this.pendingImageSrc && this.refs.baseImage.src !== this.pendingImageSrc) return;
    this.refs.stage.classList.remove('is-loading');
    this.refs.stageError.hidden = false;
  }

  handleCartAdded(event) {
    // Only react to adds we triggered (the panel is the only personalised form).
    if (!this.classList.contains('is-open')) return;
    // Persist the drawing for the new line so it can be restored from the cart.
    // Must run BEFORE resetDrawing() or the cart line would keep no drawing.
    const lineKey = event?.detail?.item?.key;
    if (lineKey) this.saveLineDesign(lineKey);
    this.showToast('ADDED TO YOUR BASKET');
    // Wipe to a blank front view so a reopen starts a fresh design, then close:
    // leaving the panel open made it unclear the item had been added at all.
    // The toast lives on <body> now, so it survives the panel closing.
    this.resetDrawing();
    this.close();
  }

  /**
   * Clear the live in-memory drawing back to a blank default state after an add.
   * Also drops the manual "DESIGN SAVED" entry so a later PDP load starts blank.
   * The per-cart-line entry (LINE_STORAGE_PREFIX) is left intact — it was just
   * saved for the added order and powers editing that line from the cart.
   */
  resetDrawing() {
    // Abandon any half-drawn stroke before swapping the side arrays out.
    this.activeStroke = null;
    this.activePointerId = null;

    // Drop the saved design so restoreSaved() on the next load finds nothing.
    try {
      localStorage.removeItem(this.storageKey);
    } catch (_error) {
      // storage unavailable — in-memory reset below still gives a blank canvas
    }

    // Clear both sides' strokes + redo, then re-point the active aliases at the
    // replaced arrays (mirrors applyPayload — a bare reassignment would detach).
    this.sides.front = { strokes: [], redo: [] };
    this.sides.back = { strokes: [], redo: [] };

    // Notes belong to the order that was just added — clear them too.
    if (this.refs.notesField) this.refs.notesField.value = '';

    // View state back to defaults: front side, brush tool, 1x zoom.
    this.useSide('front');
    this.syncImage(this.baseUrl('front'), true);

    this.state.tool = 'brush';
    this.querySelectorAll('[data-siy-tool]').forEach((btn) => {
      const active = btn.dataset.siyTool === 'brush';
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', String(active));
    });

    this.zoom = 1;
    this.refs.viewport.style.transform = 'scale(1)';
    this.updateZoomButtons();

    // Disarm the two-step Clear button if it was mid-confirm.
    window.clearTimeout(this.clearArmTimer);
    this.refs.clearButton?.classList.remove('is-armed');

    this.renderAll();
    this.updateToolButtons();
    this.updateCanvasCursor();
  }

  /* ---- Zoom -------------------------------------------------------------- */

  adjustZoom(delta) {
    const next = Math.min(3, Math.max(1, Math.round((this.zoom + delta) * 100) / 100));
    if (next === this.zoom) return;
    this.zoom = next;
    this.refs.viewport.style.transform = `scale(${this.zoom})`;
    this.updateZoomButtons();
    // The ring cursor is sized in CSS px and is not scaled by the transform above.
    this.updateCanvasCursor();
  }

  updateZoomButtons() {
    const inButton = this.querySelector('[data-siy-zoom="in"]');
    const outButton = this.querySelector('[data-siy-zoom="out"]');
    if (inButton) inButton.disabled = this.zoom >= 3;
    if (outButton) outButton.disabled = this.zoom <= 1;
  }

  /* ---- Canvas ------------------------------------------------------------ */

  resizeCanvas() {
    const img = this.refs.baseImage;
    // offset* is the untransformed layout size — unaffected by the zoom
    // transform on the viewport, so the backing store stays at layout
    // resolution while the transform handles visual scaling. The canvas is
    // pinned over the image via CSS (position:absolute; inset:0).
    const width = img.offsetWidth;
    const height = img.offsetHeight;
    if (!width || !height) return;
    const dpr = window.devicePixelRatio || 1;
    const canvas = this.refs.canvas;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    this.renderAll();
  }

  pointFromEvent(event) {
    const rect = this.refs.canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  }

  startStroke(event) {
    if (this.refs.stage.classList.contains('is-loading')) return;
    if (this.activeStroke) return;
    if (!this.ctx || !this.refs.canvas.width) return;
    event.preventDefault();
    this.refs.canvas.setPointerCapture(event.pointerId);
    this.activePointerId = event.pointerId;
    const rect = this.refs.canvas.getBoundingClientRect();
    this.activeStroke = {
      tool: this.state.tool,
      color: this.state.drawColor,
      // size stored as a fraction of the rendered width so it scales to any resolution
      size: this.toolWidth() / rect.width,
      points: [this.pointFromEvent(event)],
    };
    this.renderAll();
  }

  extendStroke(event) {
    if (!this.activeStroke) return;
    if (event.pointerId !== this.activePointerId) return;
    event.preventDefault();
    this.activeStroke.points.push(this.pointFromEvent(event));
    this.renderAll();
  }

  endStroke(event) {
    if (!this.activeStroke) return;
    if (event.pointerId !== this.activePointerId) return;
    this.refs.canvas.releasePointerCapture?.(event.pointerId);
    if (this.activeStroke.points.length > 1) {
      this.state.strokes.push(this.activeStroke);
      this.state.redo = [];
    }
    this.activeStroke = null;
    this.activePointerId = null;
    this.renderAll();
    this.updateToolButtons();
  }

  /** Stroke width in CSS px for the currently selected tool. */
  toolWidth() {
    return this.state.tool === 'eraser' ? ERASER_SIZE : this.state.brushSize;
  }

  /**
   * The eraser gets a ring cursor matching the area it will clear; the brush keeps
   * the crosshair from the stylesheet. A cursor image is NOT scaled by the
   * viewport's CSS transform, so the zoom factor has to be applied by hand or the
   * ring stops matching the stroke once the customer zooms in.
   */
  updateCanvasCursor() {
    const canvas = this.refs.canvas;
    if (!canvas) return;
    if (this.state.tool !== 'eraser') {
      canvas.style.cursor = '';
      return;
    }
    // Browsers refuse to load an oversized cursor image and silently fall back
    // to the default arrow, so cap it well inside the ~128px limit.
    const d = Math.min(96, Math.round(ERASER_SIZE * this.zoom));
    const c = d / 2;
    // Two rings, dark over light, so the outline stays visible against both the
    // ivory candle base and a dark stroke underneath it.
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${d}" height="${d}">` +
      `<circle cx="${c}" cy="${c}" r="${c - 1.5}" fill="none" stroke="#fff" stroke-width="2"/>` +
      `<circle cx="${c}" cy="${c}" r="${c - 1.5}" fill="none" stroke="${FALLBACK_SLATE}" stroke-width="1"/>` +
      `</svg>`;
    canvas.style.cursor = `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${c} ${c}, crosshair`;
  }

  renderStroke(ctx, stroke, width, height) {
    if (stroke.points.length < 2) return;
    ctx.globalCompositeOperation = stroke.tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = Math.max(1, stroke.size * width);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(stroke.points[0].x * width, stroke.points[0].y * height);
    for (let i = 1; i < stroke.points.length; i += 1) {
      ctx.lineTo(stroke.points[i].x * width, stroke.points[i].y * height);
    }
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }

  renderAll() {
    if (!this.ctx) return;
    const canvas = this.refs.canvas;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    const all = this.activeStroke ? [...this.state.strokes, this.activeStroke] : this.state.strokes;
    for (const stroke of all) {
      this.renderStroke(this.ctx, stroke, canvas.width, canvas.height);
    }
  }

  updateToolButtons() {
    const undoButton = this.querySelector('[data-siy-action="undo"]');
    const redoButton = this.querySelector('[data-siy-action="redo"]');
    if (undoButton) undoButton.disabled = this.state.strokes.length === 0;
    if (redoButton) redoButton.disabled = this.state.redo.length === 0;
    this.refs.clearButton.disabled = this.state.strokes.length === 0;
    if (this.refs.clearButton.disabled && this.refs.clearButton.classList.contains('is-armed')) {
      window.clearTimeout(this.clearArmTimer);
      this.refs.clearButton.classList.remove('is-armed');
    }
  }

  undo() {
    if (!this.state.strokes.length) return;
    this.state.redo.push(this.state.strokes.pop());
    this.renderAll();
    this.updateToolButtons();
  }

  redo() {
    if (!this.state.redo.length) return;
    this.state.strokes.push(this.state.redo.pop());
    this.renderAll();
    this.updateToolButtons();
  }

  showToast(message) {
    const toast = this.toastEl;
    if (!toast) return;
    toast.hidden = true;
    toast.textContent = '';
    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => {
      toast.hidden = true;
    }, 2500);
  }

  /** Editable vector payload (both sides) shared by localStorage save + restore. */
  strokesPayload() {
    return {
      v: 3,
      sides: {
        front: this.serializeStrokes(this.sides.front.strokes),
        back: this.serializeStrokes(this.sides.back.strokes),
      },
    };
  }

  serializeStrokes(strokes) {
    return strokes.map((s) => ({
      tool: s.tool,
      color: s.color,
      size: Number(s.size.toFixed(5)),
      points: s.points.map((p) => ({
        x: Number(p.x.toFixed(4)),
        y: Number(p.y.toFixed(4)),
      })),
    }));
  }

  saveDesign() {
    const payload = { ...this.strokesPayload(), notes: this.refs.notesField.value };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
      this.showToast('DESIGN SAVED');
    } catch (_error) {
      this.showToast('COULD NOT SAVE ON THIS DEVICE');
    }
  }

  restoreSaved() {
    let payload = null;
    try {
      payload = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
    } catch (_error) {
      return; // corrupt payload — start fresh, never block render
    }
    this.applyPayload(payload);
  }

  /** Rehydrate both sides' strokes + notes from a payload (localStorage / restore). */
  applyPayload(payload) {
    if (!payload) return;
    let front = [];
    let back = [];
    if (payload.sides && (Array.isArray(payload.sides.front) || Array.isArray(payload.sides.back))) {
      front = Array.isArray(payload.sides.front) ? payload.sides.front : [];
      back = Array.isArray(payload.sides.back) ? payload.sides.back : [];
    } else if (Array.isArray(payload.strokes)) {
      front = payload.strokes; // legacy v2 single-layer design → front
    } else {
      return;
    }
    this.sides.front.strokes = front.map((s) => this.sanitizeStroke(s)).filter((s) => s !== null);
    this.sides.front.redo = [];
    this.sides.back.strokes = back.map((s) => this.sanitizeStroke(s)).filter((s) => s !== null);
    this.sides.back.redo = [];
    // re-point the active aliases at the replaced arrays
    this.state.strokes = this.sides[this.activeSide].strokes;
    this.state.redo = this.sides[this.activeSide].redo;
    if (typeof payload.notes === 'string') this.refs.notesField.value = payload.notes;
    this.renderAll();
    this.updateToolButtons();
  }

  /** Returns a clean stroke or null. Guards the localStorage / export contract. */
  sanitizeStroke(stroke) {
    if (!stroke || typeof stroke !== 'object') return null;
    if (!Number.isFinite(stroke.size) || stroke.size <= 0) return null;
    if (!Array.isArray(stroke.points)) return null;
    const points = stroke.points.filter(
      (p) => p && Number.isFinite(p.x) && Number.isFinite(p.y)
    );
    if (points.length < 2) return null;
    return {
      tool: stroke.tool === 'eraser' ? 'eraser' : 'brush',
      color: typeof stroke.color === 'string' ? stroke.color : this.slate,
      size: stroke.size,
      points,
    };
  }

  /** Resolve a preloaded off-screen base Image, waiting for load if needed. */
  loadCachedBase(side) {
    const img = this.baseImageCache?.[side];
    if (!img) return Promise.reject(new Error('base-missing'));
    if (img.complete && img.naturalWidth) return Promise.resolve(img);
    return new Promise((resolve, reject) => {
      img.addEventListener(
        'load',
        () => (img.naturalWidth ? resolve(img) : reject(new Error('base-load'))),
        { once: true }
      );
      img.addEventListener('error', () => reject(new Error('base-load')), { once: true });
    });
  }

  async exportSide(side) {
    const img = await this.loadCachedBase(side);
    const out = document.createElement('canvas');
    out.width = img.naturalWidth;
    out.height = img.naturalHeight;
    const ctx = out.getContext('2d');
    ctx.drawImage(img, 0, 0);
    // Strokes must render on their own transparent layer: the eraser uses
    // destination-out, which on screen only affects the drawing overlay —
    // composited directly it would punch holes through the base photo.
    const layer = document.createElement('canvas');
    layer.width = out.width;
    layer.height = out.height;
    const layerCtx = layer.getContext('2d');
    for (const stroke of this.sides[side].strokes) {
      this.renderStroke(layerCtx, stroke, layer.width, layer.height);
    }
    ctx.drawImage(layer, 0, 0); // toBlob below throws if the canvas is tainted
    return new Promise((resolve, reject) => {
      out.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('export-failed'))), 'image/png');
    });
  }

  async downloadDesign() {
    try {
      const blob = await this.exportSide(this.activeSide);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `she-is-you-${this.activeSide}.png`;
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_error) {
      this.showFormError('We could not export your design. Please try again.');
    }
  }

  showFormError(message) {
    this.refs.formError.textContent = message;
    this.refs.formError.hidden = false;
    window.clearTimeout(this.formErrorTimer);
    this.formErrorTimer = window.setTimeout(() => {
      this.refs.formError.hidden = true;
    }, 6000);
  }

  /** Two-step confirm: first click arms the button, second click within 3s clears. */
  requestClear(button) {
    if (button.classList.contains('is-armed')) {
      window.clearTimeout(this.clearArmTimer);
      button.classList.remove('is-armed');
      this.setActiveStrokes([], []);
      this.renderAll();
      this.updateToolButtons();
      return;
    }
    button.classList.add('is-armed');
    this.showToast('TAP CLEAR AGAIN TO ERASE YOUR DRAWING');
    this.clearArmTimer = window.setTimeout(() => {
      button.classList.remove('is-armed');
    }, 3000);
  }
}

if (!customElements.get('she-is-you-customizer')) {
  customElements.define('she-is-you-customizer', SheIsYouCustomizer);
}
